/**
 * Production paste sanitizer for the #1106 rich-text editor.
 *
 * Goal: keep meaningful author formatting from email/Word paste, strip the
 * Office/MSO cruft that makes pasted content look ugly and unsafe.
 *
 * Table policy (see `PasteTablePolicy`):
 * - `keep-data`: keep 2+ row × 2+ col grids (email Project/Branch rows), short
 *   link button bars, Outlook signature rows, and document tables that declare
 *   a visible border (Word / Excel / Docs), including grids wider than 6
 *   columns. Flatten email wrapper tables and one-cell spacers. Only bordered
 *   document tables are marked `data-ss-grid` so CSS can draw their grid;
 *   email tables stay borderless (see richHtmlVisualContract).
 * - `flatten-all`: flatten every pasted table (for editors with no table editor).
 * - `keep-all`: keep every pasted table as-is.
 * - all policies: keep CODE/PRE when allowCodeBlocks is true.
 */

import { rewriteUnresolvedImageSrcsToHostedUrls } from './stitchClipboardImages';
import {
  assignDetachedInnerHTML,
  serializeDetachedInnerHTML,
  SS_ATTACHMENT_SRC_ATTR,
} from '../../services/pendingInlineImagePreview';

export type PasteTablePolicy = 'flatten-all' | 'keep-data' | 'keep-all';

export type PasteSanitizeOptions = {
  /** @deprecated Prefer `tablePolicy`. When true, keeps every table (`keep-all`). */
  allowTables?: boolean;
  /**
   * How pasted `<table>` elements are handled in the TipTap lane.
   * - `flatten-all` — runner-up / Quill (default when unset)
   * - `keep-data` — keep 2+×2+ grids, signature rows, and link bars; flatten wrappers
   * - `keep-all` — keep every table (legacy `allowTables: true`)
   */
  tablePolicy?: PasteTablePolicy;
  /** Keep CODE/PRE. Default false → unwrap to text. */
  allowCodeBlocks?: boolean;
  /**
   * Treat `sitescope-attachment://...` / `sitescope-attachment-pending://...`
   * IMG src values as owned (kept). IMG itself is always structurally
   * allowed regardless of this flag — ordinary `https://` remote images and
   * owned attachment refs are both kept; every other src scheme is stripped.
   * Default false.
   */
  allowSitescopeAttachmentImages?: boolean;
};

const BASE_ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'DIV',
  'SPAN',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'S',
  'STRIKE',
  'DEL',
  'H1',
  'H2',
  'H3',
  'H4',
  'UL',
  'OL',
  'LI',
  'A',
  'BLOCKQUOTE',
  'HR',
  'SUB',
  'SUP',
]);

const TABLE_TAGS = new Set([
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TD',
  'TH',
]);
const CODE_TAGS = new Set(['CODE', 'PRE']);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(['href', 'title', 'rel', 'target']),
  SPAN: new Set(['style']),
  P: new Set(['style']),
  DIV: new Set(['style']),
  H1: new Set(['style']),
  H2: new Set(['style']),
  H3: new Set(['style']),
  H4: new Set(['style']),
  LI: new Set(['style']),
  TD: new Set(['style', 'colspan', 'rowspan']),
  TH: new Set(['style', 'colspan', 'rowspan']),
  TABLE: new Set(['style', 'data-ss-grid']),
  CODE: new Set(['style']),
  PRE: new Set(['style']),
  IMG: new Set(['src', 'alt', 'title', 'style', SS_ATTACHMENT_SRC_ATTR]),
};

const ALLOWED_STYLE_PROPS = new Set([
  'font-size',
  'font-weight',
  'font-style',
  'font-family',
  'text-decoration',
  'text-align',
  'color',
  'background-color',
]);

const DANGEROUS_URL = /^(javascript:|data:text\/html|vbscript:)/i;

const SITESCOPE_ATTACHMENT_SRC = /^sitescope-attachment:\/\/\d+\/?$/;
const SITESCOPE_ATTACHMENT_PENDING_SRC =
  /^sitescope-attachment-pending:\/\/[\w-]+$/;

function isAllowedSitescopeAttachmentSrc(src: string): boolean {
  const trimmed = src.trim();
  return (
    SITESCOPE_ATTACHMENT_SRC.test(trimmed) ||
    SITESCOPE_ATTACHMENT_PENDING_SRC.test(trimmed)
  );
}

const REMOTE_IMAGE_SRC = /^https?:\/\//i;

/**
 * Harness gap fix (2026-08-04): web used to delete every pasted `<img>`
 * whose `src` wasn't already one of our own `sitescope-attachment://`
 * references — including perfectly ordinary `https://` logo/icon images
 * (e.g. a vendor email's CDN-hosted images), which is why the same email
 * looked worse on web than on native for the *same* library (Tiptap vs
 * 10tap, Quill vs Pell): neither native lane strips remote image `src`
 * values at all (they only strip `data:image/` embeds — see
 * `removeRunnerUpDataImagesCommand()` / `contentHasEmbeddedBase64Image()`).
 * This keeps ordinary remote images as read-only display images (not
 * routed through `/attachments` — we don't own that URL's bytes, so there
 * is nothing to persist), matching native's existing behavior. Raster
 * `data:` images and same-origin blob previews stay in their HTML slots
 * so mixed-email photos are not dumped at the end; rehost uploads the
 * bytes. Other `data:` embeds are still stripped here — those go through the separate
 * `extractEmbeddedDataUriImages()` → `/attachments` upload path instead so
 * we don't persist raw base64 blobs into the request record. Accepted,
 * known trade-off: a remote `<img>` fires a real GET to that host when
 * rendered (the classic email "tracking pixel" concern) — same exposure a
 * browser already accepts for any pasted rich text, and not a new risk
 * introduced by this change.
 */
function isAllowedRemoteImageSrc(src: string): boolean {
  return REMOTE_IMAGE_SRC.test(src.trim());
}

const PASTED_DATA_IMAGE_SRC = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

function isSameOriginBlobSrc(src: string): boolean {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) {
    return false;
  }
  if (/^blob:https:\/\/outlook\./i.test(trimmed)) {
    return false;
  }
  const origin =
    typeof window !== 'undefined' ? window.location?.origin ?? '' : '';
  if (origin.length >= 8) {
    return trimmed.startsWith(`blob:${origin}`);
  }
  return /^blob:https?:\/\/(localhost|127\.0\.0\.1)[:/]/i.test(trimmed);
}

/** http(s) logos, same-origin blob previews, and raster data URIs we will rehost. */
function isAllowedPastedImageSrc(src: string): boolean {
  const trimmed = src.trim();
  return (
    isAllowedRemoteImageSrc(trimmed) ||
    PASTED_DATA_IMAGE_SRC.test(trimmed) ||
    isSameOriginBlobSrc(trimmed)
  );
}

function resolveTablePolicy(options?: PasteSanitizeOptions): PasteTablePolicy {
  if (options?.tablePolicy) {
    return options.tablePolicy;
  }
  if (options?.allowTables) {
    return 'keep-all';
  }
  return 'flatten-all';
}

function keepsTableStructure(options?: PasteSanitizeOptions): boolean {
  const policy = resolveTablePolicy(options);
  return policy === 'keep-all' || policy === 'keep-data';
}

function allowedTagsFor(options?: PasteSanitizeOptions): Set<string> {
  const tags = new Set(BASE_ALLOWED_TAGS);
  if (keepsTableStructure(options)) {
    for (const tag of TABLE_TAGS) {
      tags.add(tag);
    }
  }
  if (options?.allowCodeBlocks) {
    for (const tag of CODE_TAGS) {
      tags.add(tag);
    }
  }
  // Bug fix (2026-08-04): IMG used to only enter the allowed-tag set when a
  // caller opted in via `allowSitescopeAttachmentImages`. Neither editor's
  // real paste path (RichTextEditor.web.tsx's `transformPastedHTML`, nor the
  // now-retired #1106 runner-up candidate's paste handler) ever passed that
  // flag, so every
  // `<img>` — including ordinary `https://` remote images — was unwrapped
  // here by the `!allowedTags.has(tag)` branch below, before the per-src
  // `isAllowedRemoteImageSrc()` check a few lines down ever ran. That made
  // the earlier "keep remote images" fix a dead code path for real pasted
  // email content. IMG is now always structurally allowed; `src` filtering
  // (owned sitescope-attachment vs. remote http(s) vs. everything else) is
  // still fully enforced per-attribute below.
  tags.add('IMG');
  return tags;
}

const SAFE_COLOR = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%]+\))$/i;

function safeBackgroundColor(value: string): string | null {
  const color = value.trim();
  if (/expression|url\s*\(/i.test(color) || !SAFE_COLOR.test(color)) {
    return null;
  }
  return color;
}

function cleanStyle(styleValue: string): string {
  return styleValue
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .map(declaration => {
      const colon = declaration.indexOf(':');
      if (colon < 0) {
        return null;
      }
      const prop = declaration.slice(0, colon).trim().toLowerCase();
      const value = declaration.slice(colon + 1).trim();
      const normalizedProp = prop === 'background' ? 'background-color' : prop;
      const normalizedValue =
        prop === 'background' ? safeBackgroundColor(value) : value;
      if (!normalizedValue || !ALLOWED_STYLE_PROPS.has(normalizedProp)) {
        return null;
      }
      if (/expression|url\s*\(/i.test(normalizedValue)) {
        return null;
      }
      return `${normalizedProp}: ${normalizedValue}`;
    })
    .filter((entry): entry is string => entry != null)
    .join('; ');
}

function unwrapNode(node: ChildNode): void {
  const parent = node.parentNode;
  if (!parent) {
    return;
  }
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

/** Email clients wrap list text in `<p>` / `<div>` inside `<li>`, which stacks the bullet alone on one line. */
function normalizeListItemBlocks(root: Element): void {
  for (const li of Array.from(root.querySelectorAll('li'))) {
    let changed = true;
    while (changed) {
      changed = false;
      for (const block of Array.from(li.children)) {
        const tag = block.tagName.toUpperCase();
        if (tag !== 'P' && tag !== 'DIV') {
          continue;
        }
        unwrapNode(block);
        changed = true;
      }
    }
  }
}

function normalizedElementText(element: Element): string {
  return (element.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\u200B/g, '')
    .replace(/\uFEFF/g, '')
    .trim();
}

function isEmptySpacerElement(element: Element): boolean {
  const tag = element.tagName.toUpperCase();
  if (tag !== 'P' && tag !== 'DIV') {
    return false;
  }
  if (element.querySelector('img, a[href], ul, ol, table')) {
    return false;
  }
  return normalizedElementText(element).length === 0;
}

/** Gmail/Sentry/Outlook spacer rows — empty `<p><br></p>` stacks that become huge gaps in TipTap. */
function removeEmptySpacerBlocks(root: Element): void {
  for (const el of Array.from(root.querySelectorAll('p, div'))) {
    if (el.closest('pre, code, li')) {
      continue;
    }
    if (isEmptySpacerElement(el)) {
      el.remove();
    }
  }
}

function tableRowElements(table: Element): Element[] {
  const rows: Element[] = [];
  for (const child of Array.from(table.children)) {
    const tag = child.tagName.toUpperCase();
    if (tag === 'TR') {
      rows.push(child);
    }
    if (tag === 'TBODY' || tag === 'THEAD' || tag === 'TFOOT') {
      for (const row of Array.from(child.children)) {
        if (row.tagName.toUpperCase() === 'TR') {
          rows.push(row);
        }
      }
    }
  }
  return rows;
}

function cellMarkupIsEmpty(html: string): boolean {
  if (!html || html === '&nbsp;' || /^<br\s*\/?>$/i.test(html.trim())) {
    return true;
  }
  const probe = document.createElement('div');
  assignDetachedInnerHTML(probe, html);
  return (
    normalizedElementText(probe).length === 0 &&
    !probe.querySelector('img, a[href]')
  );
}

function tableRowColumnCount(row: Element): number {
  return row.querySelectorAll(':scope > td, :scope > th').length;
}

const EMAIL_IMAGE_MAX_WIDTH_PX = 280;
const EMAIL_ICON_MAX_HEIGHT_PX = 48;

function parsePixelDimension(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  const withoutUnit = trimmed.endsWith('px')
    ? trimmed.slice(0, -2).trim()
    : trimmed;
  const parsed = Number(withoutUnit);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function readDeclaredImageWidth(element: Element): number | null {
  const fromAttr = parsePixelDimension(element.getAttribute('width'));
  if (fromAttr) {
    return fromAttr;
  }
  const style = element.getAttribute('style') ?? '';
  const match = /(?:^|;)\s*width\s*:\s*([^;]+)/i.exec(style);
  return match ? parsePixelDimension(match[1]) : null;
}

function readDeclaredImageHeight(element: Element): number | null {
  const fromAttr = parsePixelDimension(element.getAttribute('height'));
  if (fromAttr) {
    return fromAttr;
  }
  const style = element.getAttribute('style') ?? '';
  const match = /(?:^|;)\s*height\s*:\s*([^;]+)/i.exec(style);
  return match ? parsePixelDimension(match[1]) : null;
}

function isTrackingPixel(width: number | null, height: number | null): boolean {
  return (width ?? 999) <= 2 && (height ?? 999) <= 2;
}

/** Email logos/icons often declare small width/height attrs; stripping them lets multi-KB assets blow up. */
function normalizePastedImage(
  element: Element,
  declaredWidth: number | null,
  declaredHeight: number | null,
): void {
  if (isTrackingPixel(declaredWidth, declaredHeight)) {
    element.remove();
    return;
  }
  element.removeAttribute('width');
  element.removeAttribute('height');
  const maxWidth = Math.min(
    declaredWidth ?? EMAIL_IMAGE_MAX_WIDTH_PX,
    EMAIL_IMAGE_MAX_WIDTH_PX,
  );
  const maxHeight =
    declaredHeight != null && declaredHeight <= EMAIL_ICON_MAX_HEIGHT_PX
      ? declaredHeight
      : Math.min(declaredHeight ?? 96, 96);
  element.setAttribute(
    'style',
    `max-width: min(100%, ${maxWidth}px); max-height: ${maxHeight}px; width: auto; height: auto; display: inline-block;`,
  );
}

function isLinkButtonBarTable(table: Element): boolean {
  const rows = tableRowElements(table).filter(tr => {
    const cells = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
    return cells.some(cell => !cellMarkupIsEmpty(cell.innerHTML));
  });
  if (rows.length !== 1) {
    return false;
  }
  const cells = Array.from(
    rows[0].querySelectorAll(':scope > td, :scope > th'),
  ).filter(cell => !cellMarkupIsEmpty(cell.innerHTML));
  if (cells.length < 2 || cells.length > 4) {
    return false;
  }
  // Slack/Sentry-style CTA row: short labeled links. Not "icon + a sentence
  // that happens to contain a pipeline number link" (GitLab green banner).
  return cells.every(cell => {
    if (!cell.querySelector('a[href]')) {
      return false;
    }
    const text = normalizedElementText(cell);
    return text.length > 0 && text.length <= 48;
  });
}

function nonEmptyRowCells(row: Element): Element[] {
  return Array.from(row.querySelectorAll(':scope > td, :scope > th')).filter(
    cell => !cellMarkupIsEmpty(cell.innerHTML),
  );
}

function isContactBlockCell(cell: Element): boolean {
  const text = normalizedElementText(cell);
  if (text.length >= 40) {
    return true;
  }
  const blocks = cell.querySelectorAll('p, div, br, tr');
  return blocks.length >= 2 && text.length >= 8;
}

/**
 * Outlook signatures: one row, logo | name/title/address | icons.
 * Not a 2-cell GitLab "icon + short link" wrapper (those still flatten).
 */
function isSignatureRowTable(table: Element): boolean {
  const rows = tableRowElements(table).filter(
    tr => nonEmptyRowCells(tr).length > 0,
  );
  if (rows.length !== 1) {
    return false;
  }
  const cells = nonEmptyRowCells(rows[0]);
  if (cells.length < 2 || cells.length > 4) {
    return false;
  }
  const imgCount = cells.filter(cell => cell.querySelector('img')).length;
  if (imgCount === 0) {
    return false;
  }
  if (isLinkButtonBarTable(table)) {
    return false;
  }
  if (cells.length >= 3) {
    return true;
  }
  return cells.some(cell => isContactBlockCell(cell));
}

const NO_VISIBLE_BORDER = /^(?:none|hidden|transparent|0|0px|0pt|0em)$/i;

/** Word/Excel/Docs grids declare a border. Email layout tables usually do not. */
function styleDeclaresVisibleBorder(style: string): boolean {
  for (const decl of style.split(';')) {
    const match =
      /^\s*border(?:-(?:top|right|bottom|left))?(?:-width|-style|-color)?\s*:\s*(.+)$/i.exec(
        decl,
      );
    if (!match) {
      continue;
    }
    const value = match[1].trim();
    if (NO_VISIBLE_BORDER.test(value)) {
      continue;
    }
    if (
      /\d/.test(value) ||
      /\b(?:solid|double|dotted|dashed|groove|ridge)\b/i.test(value)
    ) {
      return true;
    }
  }
  return false;
}

function elementDeclaresVisibleBorder(element: Element): boolean {
  const borderAttr = element.getAttribute('border');
  if (borderAttr && borderAttr.trim() !== '0') {
    return true;
  }
  return styleDeclaresVisibleBorder(element.getAttribute('style') ?? '');
}

/**
 * Excel puts the grid on `td` / `.xl65` inside `<style>`, then we delete that
 * block. Word and Docs usually inline the border, so this only fills that gap.
 */
function stylesheetDeclaresVisibleCellBorder(html: string): boolean {
  const blocks = html.match(/<style\b[\s\S]*?<\/style>/gi) ?? [];
  for (const block of blocks) {
    const rules = block.match(/[^{}]+\{[^{}]*\}/g) ?? [];
    for (const rule of rules) {
      const splitAt = rule.indexOf('{');
      const selector = rule
        .slice(0, splitAt)
        .replace(/<[^>]*>/g, '')
        .replace(/<!--/g, '');
      const body = rule.slice(splitAt + 1, rule.lastIndexOf('}'));
      if (!/(?:^|[\s,.>])(?:td|th|table)\b|\.xl\d+/i.test(selector)) {
        continue;
      }
      if (styleDeclaresVisibleBorder(body)) {
        return true;
      }
    }
  }
  return false;
}

function tableDeclaresVisibleGrid(table: Element): boolean {
  if (
    /\bMso(?:TableGrid|NormalTable)\b/i.test(table.getAttribute('class') ?? '')
  ) {
    return true;
  }
  if (elementDeclaresVisibleBorder(table)) {
    return true;
  }
  for (const row of tableRowElements(table)) {
    for (const cell of Array.from(
      row.querySelectorAll(':scope > td, :scope > th'),
    )) {
      if (elementDeclaresVisibleBorder(cell)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Layout tables (Outlook wrappers, spacers) are flattened.
 * 2+×2+ label/value grids, painted 1-row banners, signature rows, short
 * link button bars, and bordered document tables are kept.
 */
/** Excel ranges and PowerPoint tables are data grids even when Office omits the border. */
function htmlIsOfficeDataTable(html: string): boolean {
  return /ProgId\s+content=(?:Excel\.Sheet|PowerPoint\.Slide)/i.test(html);
}

const SAFE_FONT_WEIGHT = /^(?:bold|bolder|normal|[1-9]00)$/i;
const SAFE_TEXT_ALIGN = /^(?:left|center|right|justify)$/i;

function classInlineStyles(html: string): Map<string, string> {
  const paints = new Map<string, string>();
  const blocks = html.match(/<style\b[\s\S]*?<\/style>/gi) ?? [];
  for (const block of blocks) {
    const rules = block.match(/[^{}]+\{[^{}]*\}/g) ?? [];
    for (const rule of rules) {
      const splitAt = rule.indexOf('{');
      const selector = rule.slice(0, splitAt);
      const body = rule.slice(splitAt + 1, rule.lastIndexOf('}'));
      const declarations: string[] = [];
      const background = /(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i.exec(
        body,
      );
      const backgroundColor = background
        ? safeBackgroundColor(background[1])
        : null;
      if (backgroundColor) {
        declarations.push(`background-color:${backgroundColor}`);
      }
      const weight = /(?:^|;)\s*font-weight\s*:\s*([^;]+)/i.exec(body);
      if (weight && SAFE_FONT_WEIGHT.test(weight[1].trim())) {
        declarations.push(`font-weight:${weight[1].trim()}`);
      }
      const align = /(?:^|;)\s*text-align\s*:\s*([^;]+)/i.exec(body);
      if (align && SAFE_TEXT_ALIGN.test(align[1].trim())) {
        declarations.push(`text-align:${align[1].trim()}`);
      }
      if (declarations.length === 0) {
        continue;
      }
      const paint = declarations.join(';');
      for (const match of selector.matchAll(/\.([A-Za-z][\w-]*)/g)) {
        paints.set(match[1].toLowerCase(), paint);
      }
    }
  }
  return paints;
}

/**
 * PowerPoint (and some Excel formats) store the cell fill on `.oa1` / `.xl65`.
 * Those classes are stripped before the editor sees the table.
 */
function inlineClassBackgrounds(html: string): string {
  if (typeof document === 'undefined') {
    return html;
  }
  const paints = classInlineStyles(html);
  if (paints.size === 0) {
    return html;
  }
  const container = document.createElement('div');
  assignDetachedInnerHTML(container, html);
  for (const element of Array.from(container.querySelectorAll('[class]'))) {
    const names = (element.getAttribute('class') ?? '').split(/\s+/);
    const extra = names
      .map(name => paints.get(name.toLowerCase()))
      .filter((paint): paint is string => Boolean(paint));
    if (extra.length === 0) {
      continue;
    }
    const existing = element.getAttribute('style') ?? '';
    element.setAttribute(
      'style',
      `${existing}${existing.trim().length > 0 ? ';' : ''}${extra.join(';')}`,
    );
  }
  return serializeDetachedInnerHTML(container);
}

function isLayoutTable(
  table: Element,
  officeDataTable = false,
  stylesheetGrid = false,
): boolean {
  if (table.querySelector('table')) {
    return true;
  }
  const rows = tableRowElements(table).filter(tr => {
    const cells = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
    return cells.some(cell => !cellMarkupIsEmpty(cell.innerHTML));
  });
  if (rows.length === 0) {
    return true;
  }
  const colCounts = rows.map(tableRowColumnCount).filter(count => count > 0);
  const maxCols = Math.max(...colCounts);
  const minCols = Math.min(...colCounts);
  if (maxCols <= 1) {
    return !(
      rows.length >= 2 &&
      (officeDataTable || stylesheetGrid || tableDeclaresVisibleGrid(table))
    );
  }
  if (officeDataTable) {
    return false;
  }
  if (tableDeclaresVisibleGrid(table)) {
    return false;
  }
  if (rows.length === 1) {
    if (tableHasPaintedBackground(table)) {
      return false;
    }
    if (isSignatureRowTable(table) || isLinkButtonBarTable(table)) {
      return false;
    }
    return true;
  }
  if (rows.length >= 2 && minCols >= 2 && maxCols <= 6) {
    return false;
  }
  return true;
}

function tableHasPaintedBackground(table: Element): boolean {
  const nodes = [table, ...Array.from(table.querySelectorAll('td, th'))];
  return nodes.some(node => {
    const bgcolor = node.getAttribute('bgcolor');
    if (bgcolor && isVisibleBackgroundColor(bgcolor)) {
      return true;
    }
    const style = node.getAttribute('style') ?? '';
    const match = /background(?:-color)?\s*:\s*([^;]+)/i.exec(style);
    return Boolean(match && isVisibleBackgroundColor(match[1]));
  });
}

function isVisibleBackgroundColor(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'transparent' &&
    normalized !== 'white' &&
    normalized !== '#fff' &&
    normalized !== '#ffffff' &&
    normalized !== 'rgb(255, 255, 255)' &&
    normalized !== 'rgba(255, 255, 255, 1)'
  );
}

function copyBgcolorToStyle(element: Element): void {
  const bgcolor = element.getAttribute('bgcolor');
  if (!bgcolor) {
    return;
  }
  const existing = element.getAttribute('style') ?? '';
  if (/background-color\s*:/i.test(existing)) {
    return;
  }
  element.setAttribute(
    'style',
    `${existing}${existing ? '; ' : ''}background-color: ${bgcolor}`,
  );
}

const TABLE_STRUCTURE_TAGS = new Set([
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'COLGROUP',
  'TD',
  'TH',
]);

/** Word newlines between cells become extra TipTap rows and columns. */
function removeTableWhitespace(table: Element): void {
  const drop: Text[] = [];
  const visit = (element: Element) => {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === 3) {
        const text = child as Text;
        if (
          TABLE_STRUCTURE_TAGS.has(element.tagName) &&
          text.data.trim().length === 0
        ) {
          drop.push(text);
        }
        continue;
      }
      if (child.nodeType === 1) {
        visit(child as Element);
      }
    }
  };
  visit(table);
  for (const text of drop) {
    text.remove();
  }
}

function cellSpan(cell: Element, name: 'colspan' | 'rowspan'): number {
  const parsed = Number.parseInt(cell.getAttribute(name) ?? '1', 10);
  return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
}

function isPlainEmptyCell(cell: Element): boolean {
  return (
    cellSpan(cell, 'colspan') === 1 &&
    cellSpan(cell, 'rowspan') === 1 &&
    normalizedElementText(cell).length === 0 &&
    cell.querySelector('img') == null
  );
}

function rowSpanSum(row: Element): number {
  return Array.from(row.querySelectorAll(':scope > td, :scope > th')).reduce(
    (sum, cell) => sum + cellSpan(cell, 'colspan'),
    0,
  );
}

function directCells(row: Element): Element[] {
  return Array.from(row.querySelectorAll(':scope > td, :scope > th'));
}

/** Office repeats the cells a colspan already covers. Drop only that excess. */
function removeHorizontallyCoveredCells(table: Element): void {
  const rows = tableRowElements(table);
  if (rows.length === 0) {
    return;
  }
  const base = Math.min(...rows.map(rowSpanSum));
  for (const row of rows) {
    const covered = horizontalCoveredEmpties(row);
    const excess = rowSpanSum(row) - base;
    if (excess <= 0 || excess !== covered.length) {
      continue;
    }
    for (const cell of covered) {
      cell.remove();
    }
  }
}

function horizontalCoveredEmpties(row: Element): Element[] {
  const cells = directCells(row);
  const covered: Element[] = [];
  cells.forEach((cell, index) => {
    const colspan = cellSpan(cell, 'colspan');
    if (colspan < 2) {
      return;
    }
    const following = cells.slice(index + 1, index + colspan);
    for (const candidate of following) {
      if (!isPlainEmptyCell(candidate)) {
        break;
      }
      covered.push(candidate);
    }
  });
  return covered;
}

/** Drop an empty cell that sits in a column a rowspan above already occupies. */
function removeVerticallyCoveredCells(table: Element): void {
  const rows = tableRowElements(table);
  const base = rows.length === 0 ? 0 : Math.min(...rows.map(rowSpanSum));
  const coverRemaining: number[] = [];
  for (const row of rows) {
    const coversAtStart = coverRemaining.slice();
    const activeCovers = coversAtStart.filter(count => count > 0).length;
    const cells = directCells(row);
    const coveredEmpties = cells.filter((cell, index) => {
      const column = cells
        .slice(0, index)
        .reduce((sum, previous) => sum + cellSpan(previous, 'colspan'), 0);
      return (coversAtStart.at(column) ?? 0) > 0 && isPlainEmptyCell(cell);
    });
    const expected = Math.max(0, base - activeCovers);
    const excess = cells.length - expected;
    if (excess > 0 && excess === coveredEmpties.length) {
      for (const cell of coveredEmpties) {
        cell.remove();
      }
    }
    const next = coversAtStart.map(count => Math.max(0, count - 1));
    let column = 0;
    for (const cell of directCells(row)) {
      while ((coversAtStart.at(column) ?? 0) > 0) {
        column += 1;
      }
      const colspan = cellSpan(cell, 'colspan');
      const rowspan = cellSpan(cell, 'rowspan');
      for (let offset = 0; offset < colspan; offset += 1) {
        next[column + offset] = rowspan > 1 ? rowspan - 1 : 0;
      }
      column += colspan;
    }
    coverRemaining.splice(0, coverRemaining.length, ...next);
  }
}

function normalizeKeptDataTable(table: Element, stylesheetGrid: boolean): void {
  removeTableWhitespace(table);
  removeHorizontallyCoveredCells(table);
  removeVerticallyCoveredCells(table);
  if (tableDeclaresVisibleGrid(table) || stylesheetGrid) {
    table.setAttribute('data-ss-grid', '1');
  }
  copyBgcolorToStyle(table);
  for (const cell of Array.from(table.querySelectorAll('td, th'))) {
    copyBgcolorToStyle(cell);
  }
  for (const attr of [
    'width',
    'height',
    'border',
    'cellpadding',
    'cellspacing',
    'role',
    'align',
    'bgcolor',
  ]) {
    table.removeAttribute(attr);
  }
  for (const tr of Array.from(tableRowElements(table))) {
    const cells = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
    if (cells.every(cell => cellMarkupIsEmpty(cell.innerHTML))) {
      tr.remove();
    }
  }
}

function flattenLayoutTable(table: Element): void {
  const parent = table.parentNode;
  if (!parent) {
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const tr of tableRowElements(table)) {
    const parts = Array.from(tr.querySelectorAll(':scope > td, :scope > th'))
      .map(cell => cell.innerHTML.trim())
      .filter(html => !cellMarkupIsEmpty(html));
    if (parts.length === 0) {
      continue;
    }

    if (tableRowElements(table).length === 1 && parts.length > 1) {
      const cellParagraph = document.createElement('p');
      cellParagraph.innerHTML = parts.join(' ');
      fragment.appendChild(cellParagraph);
      continue;
    }

    const p = document.createElement('p');
    if (parts.length === 2) {
      const labelProbe = document.createElement('div');
      labelProbe.innerHTML = parts[0];
      const labelText = normalizedElementText(labelProbe);
      const labelIsShort =
        labelText.length > 0 &&
        labelText.length <= 48 &&
        !/<(?:p|div|table|ul|ol|br|img)\b/i.test(parts[0]);
      p.innerHTML = labelIsShort
        ? `<strong>${parts[0]}</strong> ${parts[1]}`
        : `${parts[0]} ${parts[1]}`;
    } else {
      p.innerHTML = parts.join(' ');
    }
    fragment.appendChild(p);
  }

  if (fragment.childNodes.length === 0) {
    unwrapNode(table);
    return;
  }

  parent.insertBefore(fragment, table);
  table.remove();
}

function flattenAllLayoutTables(
  root: Element,
  options?: PasteSanitizeOptions,
  stylesheetGrid = false,
  officeDataTable = false,
): void {
  const policy = resolveTablePolicy(options);
  if (policy === 'keep-all') {
    return;
  }
  let guard = 0;
  while (guard < 100) {
    guard += 1;
    const tables = Array.from(root.querySelectorAll('table')).filter(
      table => !table.hasAttribute('data-paste-keep-table'),
    );
    if (tables.length === 0) {
      break;
    }
    let target: Element | null = null;
    for (const table of tables) {
      if (!table.querySelector('table')) {
        target = table;
        break;
      }
    }
    if (!target) {
      target = tables[0] ?? null;
    }
    if (!target) {
      break;
    }
    if (
      policy === 'flatten-all' ||
      isLayoutTable(target, officeDataTable, stylesheetGrid)
    ) {
      flattenLayoutTable(target);
      continue;
    }
    normalizeKeptDataTable(target, stylesheetGrid || officeDataTable);
    target.setAttribute('data-paste-keep-table', '1');
  }
  for (const table of Array.from(
    root.querySelectorAll('[data-paste-keep-table]'),
  )) {
    table.removeAttribute('data-paste-keep-table');
  }
}

const BLOCK_WHITESPACE_SIBLING =
  /^(?:DIV|P|TABLE|UL|OL|H[1-6]|BLOCKQUOTE|SECTION|ARTICLE|BODY|HTML|HEAD)$/;

/**
 * Word's HTML document is mostly blank lines around the fragment. Those text
 * nodes become one pre-wrap paragraph, so a pasted grid sits under a stack of
 * empty lines. Spaces between inline tags stay.
 */
function removeBlockGapWhitespace(root: Element): void {
  const drop: Text[] = [];
  const visit = (element: Element) => {
    const tag = element.tagName.toUpperCase();
    if (tag === 'PRE' || tag === 'CODE') {
      return;
    }
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === 3) {
        const text = child as Text;
        if (text.data.trim().length > 0) {
          continue;
        }
        const besideBlock = Array.from(element.childNodes).some(
          sibling =>
            sibling !== text &&
            sibling.nodeType === 1 &&
            BLOCK_WHITESPACE_SIBLING.test((sibling as Element).tagName),
        );
        if (besideBlock || element === root) {
          drop.push(text);
        }
        continue;
      }
      if (child.nodeType === 1) {
        visit(child as Element);
      }
    }
  };
  visit(root);
  for (const text of drop) {
    text.remove();
  }
}

function normalizePastedLayoutInRoot(root: Element): void {
  removeEmptySpacerBlocks(root);
  normalizeListStructureInRoot(root);
  removeEmptySpacerBlocks(root);
  removeBlockGapWhitespace(root);
}

/** Outlook/Gmail often emit one `<ul><li>…</li></ul>` per row with `<p><br></p>` dividers — TipTap merges those into ghost bullets. */
function mergeAdjacentLists(root: Element, preserveEmptyBlocks = false): void {
  const lists = Array.from(root.querySelectorAll('ul, ol'));
  for (const list of lists) {
    // Detached sanitize containers are never `isConnected`; parentNode is enough.
    if (!list.parentNode) {
      continue;
    }
    let sibling = list.nextElementSibling;
    while (sibling) {
      if (isEmptySpacerElement(sibling)) {
        if (preserveEmptyBlocks) {
          break;
        }
        const next = sibling.nextElementSibling;
        sibling.remove();
        sibling = next;
        continue;
      }
      const siblingTag = sibling.tagName.toUpperCase();
      const listTag = list.tagName.toUpperCase();
      if (siblingTag === listTag) {
        while (sibling.firstChild) {
          list.appendChild(sibling.firstChild);
        }
        const removed = sibling;
        sibling = sibling.nextElementSibling;
        removed.remove();
        continue;
      }
      break;
    }
  }
}

function listItemHasMeaningfulContent(li: Element): boolean {
  if (li.querySelector('img, a[href]')) {
    return true;
  }
  const text = (li.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\u200B/g, '')
    .trim();
  return text.length > 0;
}

/** Outlook often inserts spacer `<li><p>&nbsp;</p></li>` rows between real items. */
function removeEmptyListItems(root: Element): void {
  for (const li of Array.from(root.querySelectorAll('li'))) {
    if (!listItemHasMeaningfulContent(li)) {
      li.remove();
    }
  }
}

function normalizeListStructureInRoot(
  root: Element,
  preserveEmptyBlocks = false,
): void {
  normalizeListItemBlocks(root);
  removeEmptyListItems(root);
  mergeAdjacentLists(root, preserveEmptyBlocks);
  normalizeListItemBlocks(root);
  removeEmptyListItems(root);
}

export type RichListStructureOptions = {
  /**
   * Editor value path: keep blank blocks the app authored (assist insert gap).
   * Paste leaves this off so Gmail/Outlook spacer stacks are still dropped.
   */
  preserveEmptyBlocks?: boolean;
};

/** Unwrap block tags inside list items and drop spacer rows (paste + load). */
export function normalizeRichListStructure(
  html: string,
  options?: RichListStructureOptions,
): string {
  if (!html.trim()) {
    return html;
  }
  if (typeof document === 'undefined') {
    return html;
  }
  const container = document.createElement('div');
  assignDetachedInnerHTML(container, html);
  const preserveEmptyBlocks = options?.preserveEmptyBlocks ?? false;
  if (!preserveEmptyBlocks) {
    removeEmptySpacerBlocks(container);
  }
  normalizeListStructureInRoot(container, preserveEmptyBlocks);
  if (!preserveEmptyBlocks) {
    removeEmptySpacerBlocks(container);
  }
  return serializeDetachedInnerHTML(container);
}

function copyCellAlignToStyle(element: Element): void {
  const align = (element.getAttribute('align') ?? '').trim().toLowerCase();
  if (!SAFE_TEXT_ALIGN.test(align)) {
    return;
  }
  const existing = element.getAttribute('style') ?? '';
  if (/text-align\s*:/i.test(existing)) {
    return;
  }
  element.setAttribute(
    'style',
    `${existing}${existing.trim().length > 0 ? ';' : ''}text-align:${align}`,
  );
}

function sanitizeElement(
  element: Element,
  allowedTags: Set<string>,
  options?: PasteSanitizeOptions,
): void {
  const tag = element.tagName.toUpperCase();
  if (tag === 'TD' || tag === 'TH') {
    copyCellAlignToStyle(element);
  }
  const pastedImageWidth =
    tag === 'IMG' ? readDeclaredImageWidth(element) : null;
  const pastedImageHeight =
    tag === 'IMG' ? readDeclaredImageHeight(element) : null;

  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'META' || tag === 'LINK') {
    element.remove();
    return;
  }

  // Outlook conditional comments / Word wrappers often arrive as comments or
  // weird spans — drop empty Office-only markers.
  if (tag === 'O:P' || tag.startsWith('O:') || tag.startsWith('V:')) {
    unwrapNode(element);
    return;
  }

  if (!allowedTags.has(tag)) {
    // Keep text by unwrapping disallowed containers (e.g. TABLE → cell text).
    unwrapNode(element);
    return;
  }

  const allowed = Object.prototype.hasOwnProperty.call(ALLOWED_ATTRS, tag)
    ? ALLOWED_ATTRS[tag as keyof typeof ALLOWED_ATTRS]
    : new Set<string>();
  for (const attr of Array.from(element.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on') || name === 'class' || name === 'id') {
      element.removeAttribute(attr.name);
      continue;
    }
    if (!allowed.has(attr.name) && !allowed.has(name)) {
      element.removeAttribute(attr.name);
      continue;
    }
    if (name === 'href') {
      const href = attr.value.trim();
      if (!href || DANGEROUS_URL.test(href)) {
        element.removeAttribute(attr.name);
      } else {
        element.setAttribute('rel', 'noopener noreferrer');
        element.setAttribute('target', '_blank');
      }
    }
    if (name === 'src' && tag === 'IMG') {
      const src = attr.value.trim();
      const isOwnedAttachment = isAllowedSitescopeAttachmentSrc(src);
      if (!isOwnedAttachment && !isAllowedPastedImageSrc(src)) {
        element.remove();
        return;
      }
    }
    if (name === 'style') {
      const cleaned = cleanStyle(attr.value);
      if (cleaned) {
        element.setAttribute('style', cleaned);
      } else {
        element.removeAttribute('style');
      }
    }
  }

  if (tag === 'IMG') {
    normalizePastedImage(element, pastedImageWidth, pastedImageHeight);
  }

  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === 1) {
      sanitizeElement(child as Element, allowedTags, options);
    } else if (child.nodeType === 8) {
      // HTML comments — often MSO conditional junk.
      child.parentNode?.removeChild(child);
    }
  }
}

/**
 * Word puts `mso-*` declarations inside style attributes and often omits the
 * trailing semicolon. A document-wide strip then runs through the next tag
 * and deletes the cell markup. Only touch the attribute value.
 */
function stripMsoDeclarationsInStyleAttributes(html: string): string {
  return html.replace(
    /style=(["'])([\s\S]*?)\1/gi,
    (_full, quote: string, body: string) => {
      const cleaned = body.replace(/mso-[^:;]+:[^;]*;?/gi, '');
      return `style=${quote}${cleaned}${quote}`;
    },
  );
}

/**
 * Regex fallback used when `document` is unavailable (Node / some Jest presets).
 * The DOM path below is authoritative in the browser.
 */
function cheapSanitize(
  html: string,
  options?: PasteSanitizeOptions,
  forNodeFallback = false,
): string {
  let out = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<\/?(meta|link)[^>]*>/gi, '')
    .replace(/<\/?(o|v|w):[^>]*>/gi, '')
    .replace(/\sclass=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  out = stripMsoDeclarationsInStyleAttributes(out);
  out = out
    .replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
    .replace(/\shref\s*=\s*(["'])\s*javascript:[^"']*\1/gi, '');

  out = out.replace(/<img\b[^>]*>/gi, tag => {
    const src = readImgSrcFromTag(tag);
    const isOwnedAttachment = isAllowedSitescopeAttachmentSrc(src);
    return isOwnedAttachment || isAllowedPastedImageSrc(src) ? tag : '';
  });

  // Browser paste uses DOM flattening (`flattenAllLayoutTables`) instead.
  if (forNodeFallback && resolveTablePolicy(options) === 'flatten-all') {
    out = out.replace(/<\/?(table|thead|tbody|tfoot|tr|td|th)\b[^>]*>/gi, ' ');
  }
  if (!options?.allowCodeBlocks) {
    out = out.replace(/<\/?(code|pre)\b[^>]*>/gi, ' ');
  }
  return out;
}

/**
 * Email clients paint logos as a cell `background` or CSS `url()`, and Outlook
 * hides a second copy inside a conditional comment. Turn those into real
 * `<img>` tags before the sanitizer drops the url and keeps only the fill.
 * `cid:` / `file:` stay as slots so the clipboard stitcher can fill them.
 */
export function promoteEmailImageSlots(html: string): string {
  if (!html.trim()) {
    return html;
  }
  return collapseAdjacentDuplicatePictures(
    collapseDuplicatePicturesInCells(
      injectBackgroundImages(hoistCommentHiddenImages(html)),
    ),
  );
}

function readImgSrcFromTag(tag: string): string {
  const quoted = /\bsrc\s*=\s*(["'])([\s\S]*?)\1/i.exec(tag);
  if (quoted?.[2] != null) {
    return quoted[2].trim();
  }
  const unquoted = /\bsrc\s*=\s*([^\s>]+)/i.exec(tag);
  return (unquoted?.[1] ?? '').replace(/["']/g, '').trim();
}

function isPromotableEmailImageSrc(src: string): boolean {
  const trimmed = src.trim();
  if (
    !trimmed ||
    /^(?:javascript:|vbscript:|data:text)/i.test(trimmed) ||
    /expression\s*\(/i.test(trimmed)
  ) {
    return false;
  }
  return (
    /^https?:\/\//i.test(trimmed) ||
    /^cid:/i.test(trimmed) ||
    /^file:/i.test(trimmed) ||
    PASTED_DATA_IMAGE_SRC.test(trimmed) ||
    isRelativeOfficeImageSrc(trimmed)
  );
}

/** Word `v:imagedata` often points at `clip_image001.png` with no scheme. */
function isRelativeOfficeImageSrc(src: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:/i.test(src)) {
    return false;
  }
  return /\.(?:png|jpe?g|gif|webp|bmp)$/i.test(src);
}

/** Word's VML picture and its fallback `<img>` are the same photo. */
function isOfficePictureSlot(src: string): boolean {
  return (
    /^cid:/i.test(src) || /^file:/i.test(src) || isRelativeOfficeImageSrc(src)
  );
}

function officePictureName(src: string): string | null {
  if (!isOfficePictureSlot(src)) {
    return null;
  }
  const trimmed = src.trim();
  if (!trimmed) {
    return null;
  }
  const withoutCid = /^cid:/i.test(trimmed) ? trimmed.slice(4) : trimmed;
  const base =
    withoutCid.split(/[?#]/)[0]?.split('@')[0]?.split(/[/\\]/).pop() ?? '';
  if (
    !/\.(?:png|jpe?g|gif|webp|bmp)$/i.test(base) &&
    !/^clip_image/i.test(base)
  ) {
    return null;
  }
  return base.toLowerCase();
}

function picturesAreTheSame(left: string, right: string): boolean {
  if (left.length > 0 && left === right) {
    return true;
  }
  const leftName = officePictureName(left);
  const rightName = officePictureName(right);
  if (leftName != null && leftName === rightName) {
    return true;
  }
  return (
    (isOfficePictureSlot(left) && !isOfficePictureSlot(right)) ||
    (!isOfficePictureSlot(left) && isOfficePictureSlot(right))
  );
}

/**
 * Word puts one photo in a cell twice: the VML slot and the fallback image.
 * Keep the picture the browser can already show.
 */
function collapseDuplicatePicturesInCells(html: string): string {
  return html.replace(/<(td|th)\b[^>]*>[\s\S]*?<\/\1>/gi, cell => {
    let next = cell;
    for (let guard = 0; guard < 6; guard += 1) {
      const imgs = Array.from(next.matchAll(/<img\b[^>]*>/gi));
      if (imgs.length < 2) {
        break;
      }
      let removed = false;
      for (let index = 0; index < imgs.length - 1; index += 1) {
        const first = imgs[index];
        const second = imgs[index + 1];
        if (!first || !second) {
          continue;
        }
        const between = next.slice(
          (first.index ?? 0) + first[0].length,
          second.index ?? 0,
        );
        const text = between
          .replace(/<!--[\s\S]*?-->/g, '')
          .replace(/<[^>]+>/g, '')
          .replace(/&(?:nbsp|#0*160|#x0*a0);/gi, ' ')
          .replace(/\s+/g, '')
          .trim();
        if (text.length > 0) {
          continue;
        }
        const left = readImgSrcFromTag(first[0]);
        const right = readImgSrcFromTag(second[0]);
        if (!picturesAreTheSame(left, right)) {
          continue;
        }
        const dropFirst =
          isOfficePictureSlot(left) && !isOfficePictureSlot(right);
        const target = dropFirst ? first : second;
        const at = target.index ?? 0;
        next = next.slice(0, at) + next.slice(at + target[0].length);
        removed = true;
        break;
      }
      if (!removed) {
        break;
      }
    }
    return next;
  });
}

/**
 * Windows Word leaves the VML slot and the fallback picture next to each
 * other under the table, outside any cell. Collapse that pair too. A table
 * boundary between the two tags means they belong to different cells.
 */
function collapseAdjacentDuplicatePictures(html: string): string {
  let next = html;
  for (let guard = 0; guard < 6; guard += 1) {
    const imgs = Array.from(next.matchAll(/<img\b[^>]*>/gi));
    if (imgs.length < 2) {
      break;
    }
    let removed = false;
    for (let index = 0; index < imgs.length - 1; index += 1) {
      const first = imgs[index];
      const second = imgs[index + 1];
      if (!first || !second) {
        continue;
      }
      const between = next.slice(
        (first.index ?? 0) + first[0].length,
        second.index ?? 0,
      );
      if (/<\/?(?:td|th|tr|table)\b/i.test(between)) {
        continue;
      }
      const text = between
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&(?:nbsp|#0*160|#x0*a0);/gi, ' ')
        .replace(/\s+/g, '')
        .trim();
      if (text.length > 0) {
        continue;
      }
      const left = readImgSrcFromTag(first[0]);
      const right = readImgSrcFromTag(second[0]);
      if (!picturesAreTheSame(left, right)) {
        continue;
      }
      const dropFirst =
        isOfficePictureSlot(left) && !isOfficePictureSlot(right);
      const target = dropFirst ? first : second;
      const at = target.index ?? 0;
      next = next.slice(0, at) + next.slice(at + target[0].length);
      removed = true;
      break;
    }
    if (!removed) {
      break;
    }
  }
  return next;
}

function imageTag(src: string): string {
  const escaped = src
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
  return `<img src="${escaped}" alt="">`;
}

function htmlHasImgSrc(html: string, src: string): boolean {
  const needle = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(
    `<img\\b[^>]*\\bsrc\\s*=\\s*(?:["']${needle}["']|${needle}(?:\\s|>))`,
    'i',
  ).test(html);
}

function hoistCommentHiddenImages(html: string): string {
  const outside = html.replace(/<!--[\s\S]*?-->/g, '');
  return html.replace(/<!--([\s\S]*?)-->/g, (comment, inner: string) => {
    const tags = Array.from(
      inner.matchAll(/<(?:v:imagedata|v:fill|img)\b[^>]*>/gi),
    );
    const seen = new Set<string>();
    const imgs: string[] = [];
    for (const tag of tags) {
      const src = readImgSrcFromTag(tag[0] ?? '');
      if (!src || !isPromotableEmailImageSrc(src) || seen.has(src)) {
        continue;
      }
      if (htmlHasImgSrc(outside, src)) {
        continue;
      }
      seen.add(src);
      imgs.push(imageTag(src));
    }
    return imgs.join('') + comment;
  });
}

function backgroundImageSrcs(attrs: string): string[] {
  const found: string[] = [];
  const attr = /\sbackground\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(
    attrs,
  );
  const backgroundValue = (attr?.[1] ?? attr?.[2] ?? attr?.[3] ?? '').trim();
  if (isPromotableEmailImageSrc(backgroundValue)) {
    found.push(backgroundValue);
  }
  const styleMatch = /\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(attrs);
  const style = styleMatch?.[1] ?? styleMatch?.[2] ?? '';
  for (const match of Array.from(
    style.matchAll(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi),
  )) {
    const src = (match[2] ?? '').trim();
    if (isPromotableEmailImageSrc(src) && !found.includes(src)) {
      found.push(src);
    }
  }
  return found;
}

function stripImageUrlsFromStyle(style: string): string {
  return style
    .split(';')
    .map(part => {
      const trimmed = part.trim();
      if (!trimmed) {
        return '';
      }
      const colon = trimmed.indexOf(':');
      if (colon < 0) {
        return trimmed;
      }
      const prop = trimmed.slice(0, colon).trim().toLowerCase();
      if (prop === 'background-image') {
        return '';
      }
      if (prop === 'background') {
        const color = trimmed
          .slice(colon + 1)
          .replace(/url\(\s*(['"]?)[^'")]+?\1\s*\)/gi, '')
          .trim();
        return color ? `background: ${color}` : '';
      }
      return trimmed;
    })
    .filter(Boolean)
    .join('; ');
}

function stripBackgroundImageRefs(attrs: string): string {
  const withoutAttr = attrs.replace(
    /(\s)background\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi,
    (whole, _space: string, dq?: string, sq?: string, uq?: string) => {
      const value = (dq ?? sq ?? uq ?? '').trim();
      return isPromotableEmailImageSrc(value) ? '' : whole;
    },
  );
  return withoutAttr.replace(
    /(\sstyle\s*=\s*)(["'])([\s\S]*?)\2/gi,
    (_whole, prefix: string, quote: string, style: string) => {
      const cleaned = stripImageUrlsFromStyle(style);
      if (!cleaned.trim()) {
        return '';
      }
      return `${prefix}${quote}${cleaned}${quote}`;
    },
  );
}

function injectBackgroundImages(html: string): string {
  return html.replace(
    /<(td|th|div|table|span|p)\b([^>]*?)>/gi,
    (full: string, tagName: string, attrs: string) => {
      const srcs = backgroundImageSrcs(attrs).filter(
        src => !htmlHasImgSrc(html, src),
      );
      if (srcs.length === 0) {
        return full;
      }
      return `<${tagName}${stripBackgroundImageRefs(attrs)}>${srcs
        .map(imageTag)
        .join('')}`;
    },
  );
}

/**
 * Transform pasted HTML before the editor inserts it.
 * Safe to call from TipTap `transformPastedHTML` or Quill clipboard matchers.
 */
export function sanitizePaste(
  html: string,
  options?: PasteSanitizeOptions,
): string {
  if (!html.trim()) {
    return '';
  }

  const withImages = rewriteUnresolvedImageSrcsToHostedUrls(
    promoteEmailImageSlots(html),
  );
  const stylesheetGrid = stylesheetDeclaresVisibleCellBorder(withImages);
  const officeDataTable = htmlIsOfficeDataTable(withImages);
  const prepped = cheapSanitize(
    inlineClassBackgrounds(withImages),
    options,
    typeof document === 'undefined',
  );

  if (typeof document === 'undefined') {
    return prepped;
  }

  const allowedTags = allowedTagsFor(options);
  const container = document.createElement('div');
  assignDetachedInnerHTML(container, prepped);
  flattenAllLayoutTables(container, options, stylesheetGrid, officeDataTable);
  for (const child of Array.from(container.children)) {
    sanitizeElement(child, allowedTags, options);
  }
  normalizePastedLayoutInRoot(container);
  return serializeDetachedInnerHTML(container);
}

export function describePasteFidelity(
  html: string,
  options?: PasteSanitizeOptions,
): string {
  const hasTables = /<table[\s>]/i.test(html);
  const hasMso = /mso-|class=["']?Mso|<!--\[if/i.test(html);
  const hasImages = /<img[\s>]/i.test(html);
  const hasCode = /<(code|pre)[\s>]/i.test(html);
  const parts = [
    'Paste cleaned: keep B/I/U/S, headings, lists, links, basic size/color.',
  ];
  if (hasMso) {
    parts.push('Detected Word/Outlook markup — Office styles stripped.');
  }
  if (hasTables) {
    const policy = resolveTablePolicy(options);
    if (policy === 'keep-all') {
      parts.push('Tables kept for TipTap table extensions.');
    } else if (policy === 'keep-data') {
      parts.push('Deliberate data tables kept; email layout tables flattened.');
    } else {
      parts.push(
        'Tables flattened to text — Quill lane has no honest table editor.',
      );
    }
  }
  if (hasCode) {
    if (options?.allowCodeBlocks) {
      parts.push('Inline/code-block markup kept when present.');
    } else {
      parts.push('Code/pre tags unwrapped to plain text.');
    }
  }
  if (hasImages) {
    parts.push(
      'Remote (https) <img> kept as read-only; base64-embedded images are lifted into a real attachment via /attachments; other src schemes (cid, blob, etc.) are stripped.',
    );
  }
  return parts.join(' ');
}
